import React from 'react';
import { action, computed, observable, reaction, makeObservable } from 'mobx';
import { inject } from 'mobx-react';
import { mergeStyleSets, DefaultButton } from '@fluentui/react';
import classes from 'classnames';

import CheckboxListWithSearch from 'src/components/base/CheckboxListWithSearch';
import SearchableComboBox from 'src/components/base/SearchableComboBox';

import { ModalPage } from 'src/components/base/Page';

import FilterManager from 'src/components/Flowsteps/base/FilterManager';
import { Step } from 'src/components/Flowsteps/base/Step';
import { Button, classNames as flowstepClassNames } from 'src/components/Flowsteps/base/Flowstep';
import PullFlowstep from 'src/components/Flowsteps/PullData/PullFlowstep';
import ButtonTooltip from '@v2/components/base/ButtonTooltip';

import { runShortAction } from 'src/services/utils';
import { FlowstepType, generateFlowstepName } from 'shared/utils/flowstep';
import { includesParttern } from 'shared/utils/utils'
import {links} from 'src/constants/index';


const classNames = mergeStyleSets({
  filterManagerButton: {
    width: '100%',
  },
});

const Data = {
  Buttons: {
    [Button.Run]: { text: 'Run report', tooltip: 'Executes the chosen report and saves its Id inside of the worksheet as a reusable flow step' },
    [Button.RunAndSave]: { tooltip: 'Executes the chosen report and saves its Id inside of the worksheet as a reusable flow step' },
    [Button.Save]: { tooltip: 'Updates the report Id in the current flow step without executing the report' },
  },
  HelpLink: `https://www.xappex.com/docs/enabler4excel365/importing-data-from-salesforce-reports/`,
};


@inject('officeStore', 'dataStore')
export default class Reports extends PullFlowstep {
  @observable query = '';
  @observable queriedReports = [];

  @observable selectedReport = null;
  @observable headersFilter = '';

  @observable isReportsFetched = false;

  @observable showFilterManager = false;


  constructor(props) {
    super(props, FlowstepType.Report);
    makeObservable(this);

    this.options = { ...this.options, excludedFields: [], filters: [] };

    const baseReactions = this.setupBaseReactions();
    const reportReaction = reaction(
      () => ({ report: this.selectedReport }),
      async ({ report }) => {
        if (report) {
          //update report list on background if selected report not in recents
          (async () => this.officeStore.addLoadedReports([report]))();
          if (!this.reportFields.length) {
            await this.fetchReportHeader({ reportId: report.Id });
          }
        }
      });

    const allReactions = [...baseReactions, reportReaction];
    this.cleanReactions = () => allReactions.forEach(dispose => dispose());
  }

  @computed get _generatedName_() {
    return generateFlowstepName({ type: FlowstepType.Report, sfObjectName: this.selectedReport?.Name ?? '' });
  }

  @computed get _isValid_() {
    return super.areAllOptionsValid &&
      !!this.selectedReport &&
      !this.reportFields.length || !this.noSelectedHeaders;
  }

  @computed get _buttonsData_() { return Data.Buttons; }

  @computed get _helpArticleLink_() {
    return Data.HelpLink;
  }

  @computed get reports() {
    if (!this.props) { return [] }
    return (this.query ? this.queriedReports : this.props.officeStore.reports).map(i => ({ key: i.Id, text: i.Name }));
  }

  @computed get selectedReportExcludedFields() {
    return this.selectedReport
      ? this.options.excludedFields.reduce((out, { reportId, key}) => {
        reportId === this.selectedReport.Id && out.push(key);
        return out;
      }, [])
      : [];
  }
  
  @computed get fields() {
    return (!!this.selectedReport
      ? this.props.officeStore.getReportFieldsIfCached({ reportId: this.selectedReport.Id }) ?? []
      : [])
  }

  @computed get reportFields() {
    const filterUpper = this.headersFilter?.toUpperCase();
    return this.fields
      .filter(x => !filterUpper || x.name.toUpperCase().includes(filterUpper))
      .map(({ name, key }) => ({ key, text: name, selected: !this.selectedReportExcludedFields.includes(key) }));
  }
  

  @computed get areAllHeadersSelected() {
    return this.reportFields.every(x => x.selected);
  }

  @computed get noSelectedHeaders() {
    return this.reportFields.every(x => !x.selected);
  }

  @computed get filterFields() {
    const mapFields = (fields) => {
      return fields.map((x, index) => {
        const { name, label, dataType } = x;
        return ({
          ...x,
          id: `${index}`,
          fullName: name,
          title: name,
          fullLabel: label,
          fullDisplayName: label,
          canHaveDescendants: false,
          descendants: [],
          rawField: x
        });
      });
    }
    return mapFields(this.fields);
  }

  async _componentDidMountStart_() {
    this.isReportsFetched = false;
    await this.props.officeStore.fetchRecentReports();
  }
  
  async _componentDidMountEnd_() {
    this.isReportsFetched = true;
  }

  async _loadFlowstep_(flowstep, isExact) {
    super.loadFlowstep(flowstep, isExact);

    const { report_id: Id, report_name: Name, excludedFields, filters } = flowstep.options;
    this.selectedReport = { Id, Name };

    this.options = { ...this.options,
      excludedFields: (excludedFields ?? []).map(x => ({ key: x, reportId: Id })),
      filters: (filters ?? []),
    };
  }

  async _makeFlowstep_(makeCopy) {
    const { Name: report_name, Id: report_id } = this.selectedReport;
    const flowstep = await super.makeFlowstep(report_name);
    Object.assign(flowstep.options, {
      report_id, report_name,
      excludedFields: this.selectedReportExcludedFields,
      filters: this.options.filters,
    });

    return flowstep;
  }

  _renderBody_(renderProps) {
    const { getStepTitle } = renderProps;
    const reports = this.reports;
    const errorMessage = !reports.length && this.isReportsFetched
    && `If you don't have any reports created in Salesforce - you'll need to create and save at least one to be able to run it from here.`; 
    
    return (
      <div className={flowstepClassNames.content}>
        { this.renderCreateNewSheet(renderProps) }

        <Step title={getStepTitle('Select the report to run')}>
          <SearchableComboBox
            placeholder="Select report"
            selectedKey={this.selectedReport?.Id}
            onChange={async (event, item) => this.handleSelectReport(item)}
            instanceSearch={true}
            onSearch={this.handleReportSearch}
            options={reports}
            disabled={!this.isReportsFetched || this._readOnly_}
            errorMessage={errorMessage}
          />
        </Step>

        {this.isReportsFetched && !reports.length &&
          <Step title={''} />
        }

        <Step title={getStepTitle('Columns')}>
          <CheckboxListWithSearch
            filterPlaceholder='Filter headers'
            areAllChecked={this.areAllHeadersSelected}
            listItems={this.reportFields}
            disabled={this._readOnly_}
            onAllChecked={this.handleAllHeadersSelected}
            onItemChecked={this.handleHeaderSelected}
            onFilterChanged={this.handleHeaderSearchChanged}
          />
        </Step>

        <Step title={getStepTitle('Setup filters (optional)')}>
            <DefaultButton
              className={classNames.filterManagerButton}
              text={`Filters manager (${this.options.filters.length})`}
              onClick={this.handleOpenFiltersManager}
              disabled={!this.reportFields.length || this._readOnly_}
              primary={true}
            />
        </Step>
        
        {/* { this.renderErrorReaction(renderProps, { name: 'report', action: 'refreshed' }) } */}
        { this.renderPullModeCombobox(renderProps) }
      </div>
    );
  }

  _renderCustomPages_(renderProps) {
    return (
      <ModalPage title="Filters manager"
        key={'Page Filter manager'}
        isOpen={this.showFilterManager}
        onClose={this.handleApplyFilters}
        component={this.filterManagerComponent}
        helpLink={links.filterManagerHelp}
      />
    );
  }

  // To prevent remounting of FilterManager when this.sourceFields are changed 
  // because of reference equality check in React, save it as a member method in the class
  filterManagerComponent = () => {
    return <FilterManager
      key={'Filter manager'}
      fields={this.filterFields}
      disabled={this._readOnly_}
      filters={this.options.filters}
      onApply={this.handleApplyFilters}
      handlers={{
        onFieldsFilterApplied: this.handleFieldsFilterApplied,
        onLoadFields: Promise.resolve([]),
        onRenderFieldTitle: this.handleRenderFieldTitle,
      }}
    />
  }

  handleRenderFieldTitle = (item, nestingDepth) => {
    const { originalItem, isActive } = item;
    const { title, tooltip } = originalItem;
    return (
      <ButtonTooltip content={tooltip} styles={{ root: { width: 'auto' } }} >
        <span className={classes(flowstepClassNames.fieldText, { [flowstepClassNames.fieldTextInactive]: !isActive })}>{title}</span>
      </ButtonTooltip>
    );
  }

  handleFieldsFilterApplied = (field, filter) => {
    return includesParttern(this.options.showLabels ? field.label : field.name, filter);
  }

  @action handleReportSearch = async (query) => {
    if (query) { return await this.fetchReports(query) }

    this.selectedReport = null;
    this.query = '';
    this.queriedReports = [];
    this.setDirty();  
  }

  @action handleSelectReport = (item) => {
    this.selectedReport = item !== null ? { Id: item.key, Name: item.text } : null;
    this.query = '';
    this.queriedReports = [];
    this.setDirty();
  }

  @action handleHeaderSearchChanged = (search) => {
    this.headersFilter = search;
  }

  @action handleAllHeadersSelected = (isSelected) => {
    this.reportFields.forEach(x => this.handleHeaderSelected(isSelected, x));
  }

  @action handleHeaderSelected = (isSelected, item) => {
    this.setDirty();
    const { key } = item;
    const index = this.options.excludedFields.findIndex(({ key: fieldKey, reportId }) => fieldKey === key && this.selectedReport.Id === reportId);
    isSelected
      ? (index !== -1 && this.options.excludedFields.splice(index, 1))
      : (index === -1 && this.options.excludedFields.push({ key, reportId: this.selectedReport.Id }));
  }

  @action handleOpenFiltersManager = () => {
    this.showFilterManager = true;
  }

  @action handleApplyFilters = (filters) => {
    this.showFilterManager = false;

    if (filters) {
      this.options.filters = filters;
    }
  }

  async fetchReports(query) {
    this.query = query;
    await runShortAction(async () => {
      const reports = await this.props.officeStore.fetchReports({ query });
      if (this.query === query) { // To avoid racing issues
        this.queriedReports = reports;
      }
    });
  }

  async fetchReportHeader(data) {
    await runShortAction(async () => {
      await this.props.officeStore.fetchReportFields(data);
    });
  }

}
